import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Spin,
  message,
  Input,
  Select,
  Divider,
  Empty,
  Tabs
} from 'antd';
import {
  CheckCircleOutlined,
  PhoneOutlined,
  TeamOutlined,
  BarChartOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Column, Pie } from '@ant-design/plots';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import campaignService from '../services/campaignService';
import { resolveDepartmentGroup } from '../constants/departmentGroups';

const { Title, Text } = Typography;
const { Search } = Input;

const ReportsResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [campaignIds, setCampaignIds] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [targets, setTargets] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewTab, setViewTab] = useState('overview'); // overview | departments | organizations

  // Parse query params for filters
  const parseFilters = useCallback(() => {
    const qs = new URLSearchParams(location.search || '');
    return {
      campaignIds: qs.get('campaignIds') ? JSON.parse(qs.get('campaignIds')) : [],
      dateRange: {
        start: qs.get('start'),
        end: qs.get('end')
      },
      channel: qs.get('channel') || 'all',
      status: qs.get('status') || 'all',
      targets: qs.get('targets') ? JSON.parse(qs.get('targets')) : []
    };
  }, [location.search]);

  // Fetch aggregated data from multiple campaigns
  const fetchAggregatedStats = useCallback(async () => {
    try {
      setLoading(true);
      const filters = parseFilters();
      
      if (!filters.campaignIds || filters.campaignIds.length === 0) {
        message.info('No campaigns selected');
        setLoading(false);
        return;
      }

      // Fetch stats for each campaign and aggregate
      const statsPromises = filters.campaignIds.map(cId => {
        const params = {};
        if (filters.dateRange.start) params.start = filters.dateRange.start;
        if (filters.dateRange.end) params.end = filters.dateRange.end;
        if (filters.channel && filters.channel !== 'all') params.channel = filters.channel;
        if (filters.status && filters.status !== 'all') params.status = filters.status;
        if (filters.targets && filters.targets.length > 0) {
          params.targets = JSON.stringify(filters.targets);
        }
        
        return campaignService.getCampaignStatistics(cId, params);
      });

      const results = await Promise.all(statsPromises);
      
      // Aggregate results
      const aggregated = aggregateStats(results);
      setStats(aggregated);
      setCampaignIds(filters.campaignIds);
      
    } catch (error) {
      console.error('Error fetching aggregated stats:', error);
      message.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [parseFilters]);

  // Aggregate stats from multiple campaigns
  const aggregateStats = (statsResults) => {
    const aggregated = {
      totalTargets: 0,
      completed: 0,
      pending: 0,
      inProgress: 0,
      averageScore: 0,
      completionRate: 0,
      sms: { sent: 0, delivered: 0, failed: 0, pending: 0, deliveryRate: '0%' },
      whatsapp: { sent: 0, success: 0, failure: 0, pending: 0, successRate: '0%' },
      byDepartment: {},
      byOrganization: {}
    };

    let totalCompletedScores = 0;
    let totalCompletedCount = 0;

    statsResults.forEach(result => {
      if (!result.success || !result.data) return;

      const overview = result.data.overview || {};
      const byOrg = result.data.byOrganization || [];
      const byDept = result.data.byDepartment || [];

      // Aggregate overview stats
      aggregated.totalTargets += overview.total || 0;
      aggregated.completed += overview.completed || 0;
      aggregated.pending += overview.pending || 0;
      aggregated.inProgress += overview.inProgress || 0;

      if (overview.completed > 0) {
        totalCompletedScores += (overview.averageScore || 0) * overview.completed;
        totalCompletedCount += overview.completed;
      }

      // Aggregate SMS stats
      if (overview.sms) {
        aggregated.sms.sent += overview.sms.sent || 0;
        aggregated.sms.delivered += overview.sms.delivered || 0;
        aggregated.sms.failed += overview.sms.failed || 0;
        aggregated.sms.pending += overview.sms.pending || 0;
      }

      // Aggregate WhatsApp stats
      if (overview.whatsapp) {
        aggregated.whatsapp.sent += overview.whatsapp.sent || 0;
        aggregated.whatsapp.success += overview.whatsapp.success || 0;
        aggregated.whatsapp.failure += overview.whatsapp.failure || 0;
        aggregated.whatsapp.pending += overview.whatsapp.pending || 0;
      }

      // Aggregate by organization
      byOrg.forEach(org => {
        const key = org._id || org.organizationName || 'Unknown';
        if (!aggregated.byOrganization[key]) {
          aggregated.byOrganization[key] = {
            name: key,
            participants: 0,
            completed: 0,
            inProgress: 0,
            pending: 0,
            completionRate: 0,
            averageScore: 0
          };
        }
        aggregated.byOrganization[key].participants += org.totalParticipants || 0;
        aggregated.byOrganization[key].completed += org.totalCompleted || 0;
        aggregated.byOrganization[key].inProgress += org.inProgress || 0;
        aggregated.byOrganization[key].pending += org.pending || 0;
        aggregated.byOrganization[key].averageScore = org.averageScore || 0;
      });

      // Aggregate by department
      byDept.forEach(dept => {
        const key = dept.department || 'Unassigned';
        if (!aggregated.byDepartment[key]) {
          aggregated.byDepartment[key] = {
            name: key,
            participants: 0,
            completed: 0,
            inProgress: 0,
            pending: 0,
            completionRate: 0,
            averageScore: 0
          };
        }
        aggregated.byDepartment[key].participants += dept.totalParticipants || 0;
        aggregated.byDepartment[key].completed += dept.completedCount || 0;
        aggregated.byDepartment[key].averageScore = dept.averageScore || 0;
      });
    });

    // Calculate rates
    aggregated.completionRate = aggregated.totalTargets > 0 
      ? Math.round((aggregated.completed / aggregated.totalTargets) * 100) 
      : 0;

    aggregated.averageScore = totalCompletedCount > 0
      ? Math.round((totalCompletedScores / totalCompletedCount) * 100) / 100
      : 0;

    aggregated.sms.deliveryRate = aggregated.totalTargets > 0
      ? `${Math.round((aggregated.sms.delivered / aggregated.totalTargets) * 100)}%`
      : '0%';

    aggregated.whatsapp.successRate = aggregated.totalTargets > 0
      ? `${Math.round((aggregated.whatsapp.success / aggregated.totalTargets) * 100)}%`
      : '0%';

    // Calculate completion rates for departments and orgs
    Object.values(aggregated.byDepartment).forEach(dept => {
      dept.completionRate = dept.participants > 0
        ? Math.round((dept.completed / dept.participants) * 100)
        : 0;
    });

    Object.values(aggregated.byOrganization).forEach(org => {
      org.completionRate = org.participants > 0
        ? Math.round((org.completed / org.participants) * 100)
        : 0;
    });

    return aggregated;
  };

  useEffect(() => {
    fetchAggregatedStats();
  }, [fetchAggregatedStats]);

  // Chart data for departments
  const departmentChartData = useMemo(() => {
    if (!stats || !stats.byDepartment) return [];
    return Object.values(stats.byDepartment)
      .map(dept => ({
        department: dept.name,
        completionRate: dept.completionRate,
        averageScore: dept.averageScore,
        value: dept.completionRate,
        type: 'Completion Rate'
      }))
      .concat(
        Object.values(stats.byDepartment).map(dept => ({
          department: dept.name,
          completionRate: dept.completionRate,
          averageScore: dept.averageScore,
          value: dept.averageScore,
          type: 'Average Score'
        }))
      )
      .sort((a, b) => {
        if (a.department !== b.department) {
          return a.department.localeCompare(b.department);
        }
        return a.type.localeCompare(b.type);
      });
  }, [stats]);

  // Chart data for organizations
  const organizationChartData = useMemo(() => {
    if (!stats || !stats.byOrganization) return [];
    return Object.values(stats.byOrganization)
      .map(org => ({
        organization: org.name,
        completionRate: org.completionRate,
        participants: org.participants,
        completed: org.completed
      }))
      .sort((a, b) => b.completionRate - a.completionRate);
  }, [stats]);

  // Organization breakdown data for table
  const organizationTableData = useMemo(() => {
    if (!stats || !stats.byOrganization) return [];
    return Object.values(stats.byOrganization)
      .map((org, i) => ({
        ...org,
        key: i,
        completedPct: org.participants > 0 ? ((org.completed / org.participants) * 100).toFixed(1) : '0'
      }))
      .sort((a, b) => b.completionRate - a.completionRate);
  }, [stats]);

  // Department table data with grouping
  const departmentTableData = useMemo(() => {
    if (!stats || !stats.byDepartment) return [];
    return Object.values(stats.byDepartment)
      .map((dept, i) => ({
        ...dept,
        key: i,
        group: resolveDepartmentGroup(dept.name)
      }))
      .sort((a, b) => {
        // Sort by group first, then by completion rate
        if (a.group !== b.group) {
          return a.group.localeCompare(b.group);
        }
        return b.completionRate - a.completionRate;
      });
  }, [stats]);

  // SMS/WhatsApp delivery chart
  const deliveryChartData = useMemo(() => [
    { channel: 'SMS Delivered', value: stats?.sms?.delivered || 0 },
    { channel: 'SMS Failed', value: stats?.sms?.failed || 0 },
    { channel: 'WhatsApp Success', value: stats?.whatsapp?.success || 0 },
    { channel: 'WhatsApp Failure', value: stats?.whatsapp?.failure || 0 }
  ], [stats]);

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Loading report data...</Text>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: '24px' }}>
        <PageHeader 
          title="Multi-Campaign Report"
          breadcrumbs={["Dashboard", "Reports", "Results"]}
        />
        <Empty description="No data available for selected filters" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title="Multi-Campaign Report"
        subtitle={`Aggregated results from ${campaignIds.length} campaign${campaignIds.length !== 1 ? 's' : ''}`}
        breadcrumbs={["Dashboard", "Reports", "Results"]}
      />

      {/* Overview Statistics */}
      <Card style={{ marginBottom: '24px' }}>
        <Title level={4} style={{ marginBottom: '16px' }}>Overview</Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Total Participants"
              value={stats.totalTargets}
              prefix={<TeamOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Completion Rate"
              value={stats.completionRate}
              suffix="%"
              precision={0}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Completed"
              value={stats.completed}
              precision={0}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Average Score"
              value={stats.averageScore}
              suffix="%"
              precision={1}
            />
          </Col>
        </Row>
      </Card>

      {/* Delivery Statistics */}
      <Card style={{ marginBottom: '24px' }}>
        <Title level={4} style={{ marginBottom: '16px' }}>Delivery & Communication</Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="SMS Sent"
              value={stats.sms.sent}
              prefix={<PhoneOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="SMS Delivery Rate"
              value={stats.sms.deliveryRate}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="WhatsApp Sent"
              value={stats.whatsapp.sent}
              prefix={<PhoneOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="WhatsApp Success Rate"
              value={stats.whatsapp.successRate}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
        </Row>
      </Card>

      {/* Department Breakdown */}
      {departmentChartData.length > 0 && (
        <Card style={{ marginBottom: '24px' }}>
          <Title level={4} style={{ marginBottom: '16px' }}>Department Performance</Title>
          <Column
            data={departmentChartData}
            xField="department"
            yField="value"
            seriesField="type"
            isGroup
            label={{
              position: 'top',
              style: { fill: '#000000', opacity: 0.6, fontSize: 11 },
              formatter: (datum) => `${datum.value.toFixed(1)}%`
            }}
            legend={{ position: 'top' }}
            color={['#1890ff', '#52c41a']}
            xAxis={{ type: 'cat' }}
            yAxis={{ min: 0, max: 100, title: { text: 'Percentage (%)' } }}
            height={350}
          />
        </Card>
      )}

      {/* Organization Breakdown */}
      {organizationChartData.length > 0 && (
        <Card style={{ marginBottom: '24px' }}>
          <Title level={4} style={{ marginBottom: '16px' }}>Completion Rate by Business Unit</Title>
          <Column
            data={organizationChartData}
            xField="organization"
            yField="completionRate"
            seriesField="completionRate"
            label={{
              position: 'top',
              style: { fill: '#000000', opacity: 0.6 }
            }}
            xAxis={{ type: 'cat' }}
            yAxis={{ min: 0, max: 100 }}
            height={300}
          />
        </Card>
      )}

      {/* Department Details Table */}
      {departmentChartData.length > 0 && (
        <Card style={{ marginBottom: '24px' }}>
          <Title level={4} style={{ marginBottom: '16px' }}>Department Details</Title>
          <Table
            dataSource={departmentChartData.map((d, i) => ({ ...d, key: i }))}
            columns={[
              { title: 'Department', dataIndex: 'department', key: 'department' },
              { 
                title: 'Completion Rate', 
                dataIndex: 'completionRate',
                key: 'completionRate',
                render: (rate) => `${rate}%`
              },
              { 
                title: 'Average Score', 
                dataIndex: 'averageScore',
                key: 'averageScore',
                render: (score) => `${score.toFixed(1)}%`
              }
            ]}
            pagination={false}
          />
        </Card>
      )}

      {/* Tabs: Department / Organization / Comparison */}
      <Card style={{ marginBottom: '24px' }}>
        <Tabs
          activeKey={viewTab}
          onChange={setViewTab}
          items={[
            {
              key: 'departments',
              label: `Departments (${departmentTableData.length})`,
              children: departmentTableData.length > 0 ? (
                <div>
                  <Table
                    dataSource={departmentTableData}
                    columns={[
                      { 
                        title: 'Group', 
                        dataIndex: 'group', 
                        key: 'group',
                        sorter: (a, b) => a.group.localeCompare(b.group),
                        filters: [...new Set(departmentTableData.map(d => d.group))].map(g => ({ text: g, value: g })),
                        onFilter: (value, record) => record.group === value
                      },
                      { 
                        title: 'Department', 
                        dataIndex: 'name', 
                        key: 'name',
                        sorter: (a, b) => a.name.localeCompare(b.name)
                      },
                      { 
                        title: 'Participants', 
                        dataIndex: 'participants',
                        key: 'participants',
                        render: (v) => v || 0,
                        sorter: (a, b) => b.participants - a.participants
                      },
                      { 
                        title: 'Completed', 
                        dataIndex: 'completed',
                        key: 'completed',
                        render: (v) => v || 0,
                        sorter: (a, b) => b.completed - a.completed
                      },
                      { 
                        title: 'Completion Rate', 
                        dataIndex: 'completionRate',
                        key: 'completionRate',
                        render: (rate) => (
                          <Tag color={rate >= 75 ? 'green' : rate >= 50 ? 'orange' : 'red'}>
                            {rate}%
                          </Tag>
                        ),
                        sorter: (a, b) => b.completionRate - a.completionRate,
                        defaultSortOrder: 'descend'
                      },
                      { 
                        title: 'Average Score', 
                        dataIndex: 'averageScore',
                        key: 'averageScore',
                        render: (score) => `${score.toFixed(1)}%`,
                        sorter: (a, b) => b.averageScore - a.averageScore
                      }
                    ]}
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                </div>
              ) : <Empty description="No department data" />
            },
            {
              key: 'organizations',
              label: `Business Units (${organizationTableData.length})`,
              children: organizationTableData.length > 0 ? (
                <div>
                  <Table
                    dataSource={organizationTableData}
                    columns={[
                      { 
                        title: 'Business Unit', 
                        dataIndex: 'name', 
                        key: 'name',
                        sorter: (a, b) => a.name.localeCompare(b.name)
                      },
                      { 
                        title: 'Participants', 
                        dataIndex: 'participants',
                        key: 'participants',
                        render: (v) => v || 0,
                        sorter: (a, b) => b.participants - a.participants
                      },
                      { 
                        title: 'Completed', 
                        dataIndex: 'completed',
                        key: 'completed',
                        render: (v) => v || 0,
                        sorter: (a, b) => b.completed - a.completed
                      },
                      { 
                        title: 'Completion Rate', 
                        dataIndex: 'completionRate',
                        key: 'completionRate',
                        render: (rate) => (
                          <Tag color={rate >= 75 ? 'green' : rate >= 50 ? 'orange' : 'red'}>
                            {rate}%
                          </Tag>
                        ),
                        sorter: (a, b) => b.completionRate - a.completionRate
                      },
                      { 
                        title: 'In Progress', 
                        dataIndex: 'inProgress',
                        key: 'inProgress',
                        render: (v) => v || 0
                      },
                      { 
                        title: 'Pending', 
                        dataIndex: 'pending',
                        key: 'pending',
                        render: (v) => v || 0
                      }
                    ]}
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                </div>
              ) : <Empty description="No organization data" />
            }
          ]}
        />
      </Card>

      <div style={{ textAlign: 'center', marginTop: '24px' }}>
        <Button onClick={() => navigate('/dashboard/reports')}>
          Back to Reports
        </Button>
      </div>
    </div>
  );
};

export default ReportsResults;
